import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import ForumRiskBadge from '../ForumRiskBadge';
import { formatLeaderValue, type LeaderMetric, type RankedForum } from '../../utils/leaderboard';

interface Props {
  entries: RankedForum[];
  metric: LeaderMetric;
  darkMode: boolean;
  startRank?: number;
}

const tierStyles = (darkMode: boolean): Record<RankedForum['tier'], string> => ({
  S: darkMode
    ? 'bg-amber-900/50 text-amber-300 border-amber-700/50'
    : 'bg-amber-100 text-amber-800 border-amber-200',
  A: darkMode
    ? 'bg-indigo-900/50 text-indigo-300 border-indigo-700/50'
    : 'bg-indigo-100 text-indigo-800 border-indigo-200',
  B: darkMode
    ? 'bg-gray-700 text-gray-300 border-gray-600'
    : 'bg-gray-100 text-gray-700 border-gray-200',
  C: darkMode
    ? 'bg-gray-800 text-gray-400 border-gray-700'
    : 'bg-gray-50 text-gray-600 border-gray-200',
});

const LeaderboardRankList: React.FC<Props> = ({ entries, metric, darkMode, startRank = 4 }) => {
  const filtered = entries.filter((e) => e.rank >= startRank);
  if (filtered.length === 0) return null;

  const tiers = tierStyles(darkMode);
  const card = darkMode
    ? 'bg-gray-800/60 border-gray-700 hover:bg-gray-800'
    : 'bg-white border-gray-200 hover:border-indigo-200 shadow-sm';

  return (
    <section aria-labelledby="rank-list-heading">
      <h2
        id="rank-list-heading"
        className={`text-lg font-semibold mb-3 sm:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}
      >
        Sıralama ({startRank}–{entries[entries.length - 1]?.rank ?? startRank})
      </h2>
      <ul className="space-y-2 sm:space-y-3">
        {filtered.map((entry) => (
          <li key={entry.forum.id}>
            <Link
              to={`/forum/${entry.forum.forum_id}`}
              className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-colors ${card}`}
            >
              <span
                className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold tabular-nums ${
                  darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {entry.rank}
              </span>
              {entry.forum.logo_url && (
                <img
                  src={entry.forum.logo_url}
                  alt=""
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg object-contain shrink-0 hidden sm:block"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {entry.forum.name}
                  </span>
                  <span
                    className={`text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded border ${tiers[entry.tier]}`}
                  >
                    {entry.tier} Lig
                  </span>
                  <ForumRiskBadge forum={entry.forum} />
                </div>
                <span className={`text-xs sm:text-sm block truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {entry.forum.category}
                </span>
              </div>
              <div className="shrink-0 text-right">
                <span
                  className={`block font-bold tabular-nums text-sm sm:text-base ${
                    metric === 'growth'
                      ? 'text-green-500'
                      : darkMode
                        ? 'text-amber-300'
                        : 'text-indigo-700'
                  }`}
                >
                  {metric === 'growth' && entry.growth !== null && (
                    <ArrowUpRight className="inline mr-0.5" size={14} aria-hidden />
                  )}
                  {formatLeaderValue(metric, entry.value, entry.growth)}
                </span>
              </div>
              <ChevronRight
                className={`shrink-0 hidden sm:block ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                size={18}
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default LeaderboardRankList;
